import { useEffect, useRef, useState, useCallback } from "react";
import { Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/contexts/AuthContext";

// Auto-logout su inattività utente.
// Regole:
//   - Sessione massima 3 ore senza interazione (mouse, tastiera, scroll, touch).
//   - A 2h 59m compare un modal di avviso con countdown di 60s.
//   - Se l'utente clicca "Resta connesso" o interagisce → reset del timer.
//   - Altrimenti, allo scoccare delle 3h → logout automatico + redirect a /login.
// Attivo SOLO se loggato. Sull'altra pagina (login) non fa nulla.

const IDLE_LIMIT_MS = 3 * 60 * 60 * 1000;   // 3 ore totali
const WARNING_BEFORE_MS = 60 * 1000;        // mostra modal 60s prima dello scadere
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "wheel"];

export default function IdleLogoutMonitor() {
  const { user, logout } = useAuth();
  const [warningOpen, setWarningOpen] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(WARNING_BEFORE_MS / 1000);

  const lastActivityRef = useRef(Date.now());
  const warningTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const tickIntervalRef = useRef(null);

  const clearTimers = useCallback(() => {
    if (warningTimerRef.current) { clearTimeout(warningTimerRef.current); warningTimerRef.current = null; }
    if (logoutTimerRef.current) { clearTimeout(logoutTimerRef.current); logoutTimerRef.current = null; }
    if (tickIntervalRef.current) { clearInterval(tickIntervalRef.current); tickIntervalRef.current = null; }
  }, []);

  const scheduleTimers = useCallback(() => {
    clearTimers();
    // Schedule: warning at IDLE_LIMIT - WARNING_BEFORE, logout at IDLE_LIMIT
    warningTimerRef.current = setTimeout(() => {
      setSecondsLeft(WARNING_BEFORE_MS / 1000);
      setWarningOpen(true);
      // Tick down for UI feedback
      tickIntervalRef.current = setInterval(() => {
        setSecondsLeft((s) => Math.max(0, s - 1));
      }, 1000);
    }, IDLE_LIMIT_MS - WARNING_BEFORE_MS);

    logoutTimerRef.current = setTimeout(async () => {
      clearTimers();
      setWarningOpen(false);
      try { await logout(); } catch { /* noop */ }
      // Hard redirect to login (logout already clears in-memory user but a
      // navigation guarantees the user lands on the public route).
      if (typeof window !== "undefined") {
        window.location.href = "/login?reason=inactivity";
      }
    }, IDLE_LIMIT_MS);
  }, [clearTimers, logout]);

  const resetActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    if (warningOpen) return; // se modale aperto, l'utente deve cliccare esplicitamente
    scheduleTimers();
  }, [warningOpen, scheduleTimers]);

  // Avvio / cleanup: attivo solo se utente loggato.
  useEffect(() => {
    if (!user) {
      clearTimers();
      setWarningOpen(false);
      return undefined;
    }
    scheduleTimers();
    const handler = () => resetActivity();
    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, handler, { passive: true }));
    return () => {
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, handler));
      clearTimers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleStayConnected = () => {
    setWarningOpen(false);
    lastActivityRef.current = Date.now();
    scheduleTimers();
  };

  const handleLogoutNow = async () => {
    clearTimers();
    setWarningOpen(false);
    try { await logout(); } catch { /* noop */ }
    if (typeof window !== "undefined") {
      window.location.href = "/login";
    }
  };

  if (!user) return null;

  return (
    <AlertDialog open={warningOpen} onOpenChange={() => { /* prevent ESC close */ }}>
      <AlertDialogContent data-testid="idle-warning-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin text-amber-500" /> Sei ancora qui?
          </AlertDialogTitle>
          <AlertDialogDescription>
            Per la tua sicurezza, sarai disconnesso automaticamente tra
            {" "}
            <strong className="text-amber-600 tabular-nums">{secondsLeft}s</strong>
            {" "}
            a causa di inattività.
            <br />
            Clicca "Resta connesso" per continuare a usare l'app.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleLogoutNow} data-testid="idle-logout-now">
            Esci ora
          </AlertDialogCancel>
          <AlertDialogAction onClick={handleStayConnected} data-testid="idle-stay-connected">
            Resta connesso
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
